import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { ErrorCategory } from '../../types';
import {
  CHART_CATEGORICAL,
  CHART_ERROR,
  CHART_ERROR_DARK,
  CHART_NEUTRAL,
  CHART_ORANGE,
  CHART_SUCCESS_TEXT,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
  CHART_WARNING,
} from './chartTheme';
import { ChartEmptyState, ChartPanel, ChartSliceGradient } from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface ErrorAnalysisProps {
  errors: ErrorCategory[];
  title?: string;
}

const ERROR_COLORS: Record<string, string> = {
  'Timeout': CHART_ERROR_DARK,
  'Rate Limited': CHART_WARNING,
  'Invalid Scan': CHART_ORANGE,
  'AI Generation Error': CHART_ERROR,
  'API Error': CHART_CATEGORICAL[5],
  'Other': CHART_CATEGORICAL[2],
  'Unknown': CHART_NEUTRAL,
};

function getErrorColor(category: string): string {
  return ERROR_COLORS[category] || ERROR_COLORS['Other'];
}

export function ErrorAnalysis({ errors, title = 'Error Analysis' }: ErrorAnalysisProps) {
  const gradientBaseId = useChartGradientId('error-slice');
  const hasData = errors.length > 0;
  const totalErrors = errors.reduce((sum, e) => sum + e.count, 0);

  const chartData = errors.map(e => ({
    name: e.category,
    value: e.count,
    percent: e.percent,
    color: getErrorColor(e.category),
  }));

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-danger-50 text-danger-600"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      }
      summary={
        hasData ? (
          <div className="text-center">
            <p className="text-lg font-semibold leading-none text-danger-700">{totalErrors}</p>
            <p className="mt-1 text-[10px] uppercase text-slate-500">Errors</p>
          </div>
        ) : undefined
      }
    >

      {hasData ? (
        <>
          {/* Horizontal legend */}
          <div className="flex flex-shrink-0 flex-wrap justify-center gap-x-3 gap-y-1 border-b border-slate-200 pb-3">
            {errors.map((error) => (
              <div key={error.category} className="flex items-center gap-1">
                <div
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: getErrorColor(error.category) }}
                />
                <span className="text-[10px] text-slate-600">{error.category}</span>
                <span className="text-[10px] font-medium text-slate-700">{error.count}</span>
              </div>
            ))}
          </div>

          {/* Pie Chart */}
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  {chartData.map((entry, index) => (
                    <ChartSliceGradient
                      key={entry.name}
                      id={`${gradientBaseId}-${index}`}
                      color={entry.color}
                    />
                  ))}
                </defs>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="38%"
                  outerRadius="62%"
                  paddingAngle={2}
                  dataKey="value"
                  isAnimationActive={false}
                >
                  {chartData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={chartGradientFill(`${gradientBaseId}-${index}`)}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  formatter={(value: number, name: string) => [`${value} errors`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <ChartEmptyState>
          <div className="text-center">
            <svg className="w-10 h-10 mx-auto mb-1" style={{ color: CHART_SUCCESS_TEXT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium" style={{ color: CHART_SUCCESS_TEXT }}>
              No errors in this period!
            </p>
          </div>
        </ChartEmptyState>
      )}
    </ChartPanel>
  );
}
